import SvgSafeGatsbyImage from '@atoms/SvgSafeGatsbyImage';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import React from 'react';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import Container from '../atoms/Container';
import Nearest from '../franchise/nearest';
import { colors } from '../primitives/colors';
import Col from '../primitives/grid/col';
import Row from '../primitives/grid/row';
import RowSet from '../primitives/grid/row-set';
import { StyledButtonAsLink } from '../primitives/styled-button';
import { breakpoints, borderRadius } from '../primitives/tokens';
import Typography from '../primitives/typography';

const SplitHero = ({ data, children = null }) => {
    const {
        button_link,
        button_link_2,
        copy,
        heading,
        subheading,
        image,
        has_location_widget,
        hero_offset,
    } = data;

    const { isMicrosite } = useMicrosite();

    const showNearest = (isMicrosite || has_location_widget)

    return (
        <section css={{
            position: 'relative',
            marginTop: '28px',
            marginBottom: showNearest ? '0' : '28px',
            overflow: 'hidden',
        }}>
            <Container>
                <Row>
                    <Col breakpoints={{ dt: { span: 5, start: 7 }, mb: { span: 4, start: 0 } }}>
                        {image && (
                            <div css={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                                paddingRight: '30px',
                                [breakpoints.tb]: {
                                    paddingRight: '0',
                                },
                                [breakpoints.mb]: {
                                    marginTop: '24px'
                                },
                                '&:before': {
                                    [breakpoints.tb]: {
                                        display: 'none',
                                    },
                                    content: '""',
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    left: '0',
                                    top: '30px',
                                    zIndex: '-1',
                                    backgroundPosition: 'right top',
                                    backgroundImage: "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAAjCAYAAADxG9hnAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAACxMAAAsTAQCanBgAAAAhdEVYdENyZWF0aW9uIFRpbWUAMjAyMTowNDoyMyAxNjo1MjozOSf3SngAAABJSURBVFhH7dYxDgAQDIXhciirSzmGS1ldiqWTSCMxIP5veR2apuMTANjkNKdSjU1HyaGYu5aVO17zuGseAQAA36KhjWhowKNEOiStEAsIN9AcAAAAAElFTkSuQmCC')",
                                },
                            }}
                            >
                                <SvgSafeGatsbyImage
                                    css={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '600px',
                                        objectFit: 'cover',
                                        overflow: 'hidden',
                                        transform: 'translateZ(0)',
                                        // border: `2px solid ${colors.grey200}`,
                                        [breakpoints.tb]: {
                                            minHeight: '480px',
                                        },
                                        [breakpoints.mb]: {
                                            minHeight: '300px',
                                            maxHeight: '400px',
                                        },
                                    }}
                                    image={image}
                                />
                                {(image?.caption1 || image?.caption2) && (
                                    <div css={{
                                        textAlign: 'center',
                                        position: 'absolute',
                                        bottom: showNearest ? '28px' : '0',
                                        width: '100%',
                                        padding: '24px',
                                        paddingRight: 'calc(24px + 32px)',
                                        [breakpoints.tb]: {
                                            paddingRight: '24px',
                                        },
                                        [breakpoints.mb]: {
                                            bottom: '0'
                                        }
                                    }}
                                    >
                                        <div css={{
                                            backgroundColor: colors.shadesWhite,
                                            borderRadius: borderRadius.medium,
                                            width: '100%',
                                            padding: '12px',
                                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)',
                                        }}
                                        >
                                            {image?.caption1 && (
                                                <Typography
                                                    fontSize={{
                                                        dt: 'lg',
                                                        tb: 'base',
                                                        mb: 'sm',
                                                    }}
                                                    fontWeight="medium"
                                                >
                                                    {image.caption1}
                                                </Typography>
                                            )}
                                            {image?.caption2 && (
                                                <Typography
                                                    fontSize={{
                                                        dt: 'base',
                                                        tb: 'sm',
                                                        mb: 'xs',
                                                    }}
                                                >
                                                    {image.caption2}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Col>
                    <Col breakpoints={{ dt: { span: 6, start: 0 }, tb: { span: 7, start: 0 }, mb: { span: 4, start: 0 } }}>
                        <RowSet
                            breakpoints={{
                                dt: { between: 32 },
                                tb: { between: 24 },
                                mb: { between: 16 },
                            }}
                            css={{
                                padding: '72px 0',
                                [breakpoints.tb]: {
                                    padding: '48px 0',
                                },
                                [breakpoints.mb]: {
                                    paddingTop: '32px',
                                    paddingBottom: '0',
                                },
                            }}
                        >
                            {heading && (
                                <Typography
                                    as="h1"
                                    fontSize={{
                                        dt: '7xl',
                                        tb: '5xl',
                                        mb: '4xl',
                                    }}
                                    reducedLetterSpacing
                                    dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(heading) }}
                                />
                            )}
                            {subheading && (
                                <Typography
                                    fontSize={{
                                        dt: '4xl',
                                        tb: '3xl',
                                        mb: '2xl',
                                    }}
                                    reducedLetterSpacing
                                    dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(subheading) }}
                                />
                            )}
                            <Typography
                                fontSize={{
                                    dt: '2xl',
                                    tb: 'xl',
                                    mb: 'lg',
                                }}
                                css={RichTextEditorStyles}
                                dangerouslySetInnerHTML={{ __html: copy }}
                            />
                            {children}
                            <div css={{
                                display: 'flex',
                                flexFlow: 'wrap',
                                gap: '12px',
                            }}
                            >
                                {(button_link?.title && button_link.href) && (
                                    <StyledButtonAsLink to={button_link.href}>
                                        {button_link.title}
                                    </StyledButtonAsLink>
                                )}
                                {(button_link_2?.title && button_link_2?.href) && (
                                    <StyledButtonAsLink to={button_link_2.href}>
                                        {button_link_2.title}
                                    </StyledButtonAsLink>
                                )}
                            </div>
                        </RowSet>
                    </Col>
                </Row>
            </Container>
            {showNearest && (
                <div css={{
                    marginTop: '-32px',
                    paddingBottom: '32px',
                    position: 'relative',
                    [breakpoints.tb]: {
                        marginTop: '32px',
                        paddingBottom: '0',
                    }
                }}
                >
                    <Nearest />
                </div>
            )}
        </section>
    );
};

export default SplitHero;
